import { useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { RefObject } from 'react';

type Section = {
  id: string;
  ref: RefObject<HTMLElement>;
};

/**
 * A scroll spy hook that updates URL hash without causing rerenders
 * and preserves search parameters
 */
export default function useScrollSpy(sections: Section[], defaultSectionId: string | null = null) {
  const navigate = useNavigate();
  const location = useLocation();
  const activeSection = useRef<string | null>(defaultSectionId);
  const lastUrlUpdate = useRef<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingRef = useRef<boolean>(false);
  const pendingSectionUpdateRef = useRef<string | null>(null);

  function updateUrlWithHash(sectionId: string) {
    const url = `${location.pathname}${location.search}#${sectionId}`;
    navigate(url, { replace: true });
    lastUrlUpdate.current = sectionId;
  }

  function applyPendingSectionUpdate() {
    const sectionId = pendingSectionUpdateRef.current;
    if (sectionId) {
      activeSection.current = sectionId;

      if (lastUrlUpdate.current !== sectionId) {
        updateUrlWithHash(sectionId);
      }

      pendingSectionUpdateRef.current = null;
    }
  }

  function processSectionsVisibility(
    entries: Array<{
      element: HTMLElement;
      rect: DOMRect;
      ratio: number;
      isVisible: boolean;
    }>
  ) {
    const visibleSections = entries.filter((entry) => entry.isVisible);

    if (visibleSections.length > 0) {
      const fullyVisibleSections = visibleSections.filter((entry) => entry.ratio > 0.9);

      let targetSection;
      const isAtBottomOfPage = window.innerHeight + Math.round(window.scrollY) >= document.body.offsetHeight - 10;

      if (isAtBottomOfPage) {
        const lastSectionIds = new Set(sections.map((section) => section.id));
        const visibleLastSections = fullyVisibleSections.filter((entry) => lastSectionIds.has(entry.element.id));

        if (visibleLastSections.length > 0) {
          targetSection = visibleLastSections.reduce((previous, current) =>
            previous.rect.bottom > current.rect.bottom ? previous : current
          );
        }
      }

      if (!targetSection) {
        if (fullyVisibleSections.length > 0) {
          targetSection = fullyVisibleSections.reduce((previous, current) =>
            previous.rect.top < current.rect.top ? previous : current
          );
        } else {
          targetSection = visibleSections.reduce((previous, current) =>
            previous.ratio > current.ratio ? previous : current
          );
        }
      }

      const sectionId = targetSection.element.id;
      if (sectionId && activeSection.current !== sectionId) {
        pendingSectionUpdateRef.current = sectionId;

        if (!isScrollingRef.current) {
          applyPendingSectionUpdate();
        }
      }
    }
  }

  function checkSectionsVisibility() {
    const entries = sections
      .map((section) => {
        if (section.ref.current) {
          const element = section.ref.current;
          const rect = element.getBoundingClientRect();
          const viewHeight = window.innerHeight;
          const visibleHeight = Math.min(rect.bottom, viewHeight) - Math.max(rect.top, 0);
          const ratio = visibleHeight > 0 ? visibleHeight / element.offsetHeight : 0;

          return {
            element,
            rect,
            ratio,
            isVisible: ratio > 0.1,
          };
        }
        return null;
      })
      .filter(Boolean) as Array<{
      element: HTMLElement;
      rect: DOMRect;
      ratio: number;
      isVisible: boolean;
    }>;

    if (entries.length > 0) {
      processSectionsVisibility(entries);
    }
  }

  function handleScroll() {
    if (!isScrollingRef.current) {
      isScrollingRef.current = true;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Scrolling has stopped...
      isScrollingRef.current = false;
      checkSectionsVisibility();
      debounceTimerRef.current = null;
    }, 250);
  }

  useEffect(() => {
    if (defaultSectionId && !location.hash) {
      updateUrlWithHash(defaultSectionId);
    }

    checkSectionsVisibility();

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sections, defaultSectionId, location.hash]);

  return null;
}
